import { forwardRef, useMemo } from 'react';
import { ErrorAlert, ErrorBoundary } from '../../../components';
import { GridItemContent, GridItemContentProps } from './GridItemContent';

interface GridItemProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    Omit<GridItemContentProps, 'width' | 'height'> {
  // Children needed to ensure the drag handle gets added.
  // https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
  children?: React.ReactNode;
}

/**
 * This component acts as a grid item child within a react-grid-layout.
 *
 * Many of the props (e.g. `style`) are added to this component by `react-grid-layout` via a
 * `cloneElement`, so we have limited control over how they work. See react-grid-layout documementation for
 * additional context.
 * https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
 */
export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(function GridItem(
  { panelOptions, panelGroupItemId, gridLayout, splitVariableValue, style, children, ...otherProps },
  ref
) {
  const { width, height } = useMemo(() => {
    const initWidth = style?.width;
    const initHeight = style?.height;

    return {
      // Parse numeric values if the dimensions are strings.
      width: typeof initWidth === 'string' ? parseFloat(initWidth) : initWidth,
      height: typeof initHeight === 'string' ? parseFloat(initHeight) : initHeight,
    };
  }, [style?.height, style?.width]);

  if (typeof width !== 'number' || typeof height !== 'number') {
    // Covering this just in case for typing reasons, but it shouldn't happen in practice unless something
    // has gone terribly wrong with react-grid-layout.
    return null;
  }

  return (
    <div ref={ref} style={style} {...otherProps}>
      <div className="drag-overlay">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <h1> Move to a new panel group</h1>
        </div>
      </div>
      <ErrorBoundary FallbackComponent={ErrorAlert}>
        <GridItemContent
          panelOptions={panelOptions}
          panelGroupItemId={panelGroupItemId}
          width={width}
          height={height}
          gridLayout={gridLayout}
          splitVariableValue={splitVariableValue}
        />
      </ErrorBoundary>
      {children}
    </div>
  );
});
